//import "@stripe/stripe-js";
import React from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { ROUTES } from "./data";
import { UserProvider } from "./helpers";
import { userActions } from "./redux/action";
import { connect, useDispatch } from "react-redux";
import { useEffect, useLayoutEffect } from "react";
import { usePromiseTracker } from "react-promise-tracker";
import AdminProtectedPage from "./helpers/AdminProtectedPage";
import BuyersProtectedPages from "./pages/Buyer/BuyerProtectedPages";
import SellersProtectedPages from "./pages/Seller/SellerProtectedPages";
import { CompareBar } from "./stories/es/Compare/CompareBar";
import AnnouncementBanner from "./stories/es/AnnouncementBanner/AnnouncementBanner";
import SupportChatWidget from "./stories/es/SupportChat/SupportChatWidget";
import { CookieConsent } from "./stories/es/Privacy/CookieConsent";
import { UpsellPopup } from "./stories/es/Popup/UpsellPopup";

// ---------------------------------------------------------------------------
// Route-based code splitting: all page components are lazy-loaded so they
// end up in separate chunks and are only fetched when the user navigates
// to the corresponding route.
// ---------------------------------------------------------------------------

// Buyer pages
const Landing = React.lazy(() => import("./pages/Buyer/Landing"));
const Login = React.lazy(() => import("./pages/Buyer/Login"));
const Register = React.lazy(() => import("./pages/Buyer/Register"));
const ResetPassword = React.lazy(() => import("./pages/Buyer/ResetPassword"));
const ChangePassword = React.lazy(() => import("./pages/Buyer/ChangePassword"));
const Aboutus = React.lazy(() => import("./pages/Buyer/aboutus"));
const Account = React.lazy(() => import("./pages/Buyer/account"));
const AddPayment = React.lazy(() => import("./pages/Buyer/addPayment"));
const BuyerCatalog = React.lazy(() => import("./pages/Buyer/buyerCatalog"));
const BuyerDashboard = React.lazy(() => import("./pages/Buyer/buyerDashboard"));
const BuyerOrder = React.lazy(() => import("./pages/Buyer/buyerOrder"));
const BuyerTransaction = React.lazy(() => import("./pages/Buyer/buyerTransaction"));
const Careers = React.lazy(() => import("./pages/Buyer/careers"));
const CareersApply = React.lazy(() => import("./pages/Buyer/careersApply"));
const CareersDetail = React.lazy(() => import("./pages/Buyer/careersDetail"));
const Cart = React.lazy(() => import("./pages/Buyer/cart"));
const Checkout = React.lazy(() => import("./pages/Buyer/checkout"));
const VerifyOTP = React.lazy(() => import("./pages/Buyer/verifyOTP"));
const Inbox = React.lazy(() => import("./pages/Buyer/inbox"));
const Message = React.lazy(() => import("./pages/Buyer/message"));
const MessageSingle = React.lazy(() => import("./pages/Buyer/messageSingle"));
const Order = React.lazy(() => import("./pages/Buyer/order"));
const Pending = React.lazy(() => import("./pages/Buyer/pending"));
const ProductSingle = React.lazy(() => import("./pages/Buyer/ProductSingle"));
const Receipt = React.lazy(() => import("./pages/Buyer/receipt"));
const RecentlyViewed = React.lazy(() => import("./pages/Buyer/recentlyViewed"));
const CategoryPage = React.lazy(() => import("./pages/Buyer/category"));
const SavedItem = React.lazy(() => import("./pages/Buyer/savedItem"));
const Search = React.lazy(() => import("./pages/Buyer/Search"));
const Manufacturing = React.lazy(() => import("./pages/Buyer/Manufacturing"));
const EcoFriendlyPage = React.lazy(() => import("./pages/Buyer/EcoFriendly"));
const CompanyProfile = React.lazy(() => import("./pages/Buyer/CompanyProfile"));
const BuyerQuotes = React.lazy(() => import("./pages/Buyer/buyerQuotes"));
const BuyerSubscriptions = React.lazy(() => import("./pages/Buyer/buyerSubscriptions"));
const Compare = React.lazy(() => import("./pages/Buyer/Compare"));
const SharedWishlist = React.lazy(() => import("./pages/Buyer/SharedWishlist"));
const Track = React.lazy(() => import("./pages/Buyer/track"));
const Sponsorship = React.lazy(() => import("./pages/Buyer/sponsorship"));
const ProductDetail = React.lazy(() => import("./pages/Buyer/productDetail"));
const RateProduct = React.lazy(() => import("./pages/Buyer/rateProduct"));
const BuyerReturnsPage = React.lazy(() => import("./pages/Buyer/buyerReturns"));
const BuyerDisputesPage = React.lazy(() => import("./pages/Buyer/buyerDisputes"));
const BuyerDisputeDetailPage = React.lazy(() => import("./pages/Buyer/buyerDisputeDetail"));
const BuyerSavedSearches = React.lazy(() => import("./pages/Buyer/BuyerSavedSearches"));
const BuyerPriceAlerts = React.lazy(() => import("./pages/Buyer/BuyerPriceAlerts"));
const BuyerWaitlist = React.lazy(() => import("./pages/Buyer/BuyerWaitlist"));
const BuyerFollowing = React.lazy(() => import("./pages/Buyer/BuyerFollowing"));
const Terms = React.lazy(() => import("./pages/Buyer/Terms"));
const Privacy = React.lazy(() => import("./pages/Buyer/Privacy"));
const Contact = React.lazy(() => import("./pages/Buyer/Contact"));
const Changelog = React.lazy(() => import("./pages/Buyer/Changelog"));
const Stats = React.lazy(() => import("./pages/Buyer/Stats"));
const SuccessStoriesPage = React.lazy(() => import("./pages/Buyer/SuccessStoriesPage"));
const BackedCampaignsPage = React.lazy(() => import("./pages/Buyer/backedCampaigns"));
const PrivacyCenterPage = React.lazy(() => import("./pages/Buyer/PrivacyCenter"));
const BuyerRfqsPage = React.lazy(() => import("./pages/Buyer/buyerRfqs"));
const CreateRfqPage = React.lazy(() => import("./pages/Buyer/createRfq"));
const NotificationCenterPage = React.lazy(() => import("./pages/Buyer/notificationCenter"));
const NotificationPreferencesPage = React.lazy(() => import("./pages/Buyer/notificationPreferences"));
const BuyerProtectionPage = React.lazy(() => import("./pages/Buyer/BuyerProtection"));
const BuyerSamplesPage = React.lazy(() => import("./pages/Buyer/buyerSamples"));
const ReferralDashboardPage = React.lazy(() => import("./pages/Buyer/referralDashboard"));
const GiftCardsPage = React.lazy(() => import("./pages/Buyer/giftCards"));
const CreditWalletPage = React.lazy(() => import("./pages/Buyer/creditWallet"));
const BuyerLoyaltyPage = React.lazy(() => import("./pages/Buyer/buyerLoyalty"));

// Seller pages
const SellerDashboard = React.lazy(() => import("./pages/Seller/sellerDashboard"));
const CampaignBackersPage = React.lazy(() => import("./pages/Seller/campaignBackers"));
const SellerMessages = React.lazy(() => import("./pages/Seller/messages"));
const SellerMessagesSingle = React.lazy(() => import("./pages/Seller/messagesSingle"));
const SellerOrder = React.lazy(() => import("./pages/Seller/sellerOrder"));
const SellerOrder2 = React.lazy(() => import("./pages/Seller/sellerOrder2"));
const SellerRegistration = React.lazy(() => import("./pages/Seller/sellerRegistration"));
const SellerRegistration2 = React.lazy(() => import("./pages/Seller/sellerRegistration2"));
const SellerRegistration3 = React.lazy(() => import("./pages/Seller/sellerRegistration3"));
const Listing = React.lazy(() => import("./pages/Seller/listing"));
const ListingDetail = React.lazy(() => import("./pages/Seller/listingDetail"));
const MarketingSummary = React.lazy(() => import("./pages/Seller/marketingSummary"));
const Marketing = React.lazy(() => import("./pages/Seller/marketing"));
const MarketingCampaign = React.lazy(() => import("./pages/Seller/marketingCampaign"));
const MarketingInventory = React.lazy(() => import("./pages/Seller/marketingInventory"));
const MarketingPriceRange = React.lazy(() => import("./pages/Seller/marketingPriceRange"));
const Performance = React.lazy(() => import("./pages/Seller/performance"));
const NDAPopup = React.lazy(() => import("./pages/Seller/NDAPopup"));
const PaymentSetupPage = React.lazy(() => import("./pages/Seller/PaymentSetup"));
const NDAMessage = React.lazy(() => import("./pages/Seller/NDAMessage"));
const NDAMessageInner = React.lazy(() => import("./pages/Seller/NDAMessageInner"));
const NDAProposals = React.lazy(() => import("./pages/Seller/NDAProposals"));
const SellerVerification = React.lazy(() => import("./pages/Seller/SellerVerification"));
const SellerBoost = React.lazy(() => import("./pages/Seller/sellerBoost"));
const SellerPayouts = React.lazy(() => import("./pages/Seller/sellerPayouts"));
const SellerQuotes = React.lazy(() => import("./pages/Seller/sellerQuotes"));
const SellerStoreSettings = React.lazy(() => import("./pages/Seller/sellerStoreSettings"));
const SellerBundles = React.lazy(() => import("./pages/Seller/sellerBundles"));
const SellerCollections = React.lazy(() => import("./pages/Seller/sellerCollections"));
const BulkUpload = React.lazy(() => import("./pages/Seller/bulkUpload"));
const BulkPriceUpdatePage = React.lazy(() => import("./pages/Seller/bulkPriceUpdate"));
const BulkInventoryUpdatePage = React.lazy(() => import("./pages/Seller/bulkInventoryUpdate"));
const SellerDisputesPage = React.lazy(() => import("./pages/Seller/sellerDisputes"));
const SellerReturnsPage = React.lazy(() => import("./pages/Seller/sellerReturns"));
const ProductAnalyticsPage = React.lazy(() => import("./pages/Seller/productAnalytics"));
const SellerAdsPage = React.lazy(() => import("./pages/Seller/sellerAds"));
const SigningHistoryPage = React.lazy(() => import("./pages/Seller/signingHistory"));
const ManufacturerRfqsPage = React.lazy(() => import("./pages/Seller/manufacturerRfqs"));
const RFQBoardPage = React.lazy(() => import("./pages/Seller/rfqBoard"));
const RFQAnalyticsPage = React.lazy(() => import("./pages/Seller/rfqAnalytics"));
const SellerAnalyticsPage = React.lazy(() => import("./pages/Seller/sellerAnalytics"));
const SellerEarningsPage = React.lazy(() => import("./pages/Seller/sellerEarnings"));
const SellerSamplesPage = React.lazy(() => import("./pages/Seller/sellerSamples"));
const SellerReportsPage = React.lazy(() => import("./pages/Seller/sellerReports"));
const SellerSubscriptionPage = React.lazy(() => import("./pages/Seller/sellerSubscription"));
const SellerModerationPage = React.lazy(() => import("./pages/Seller/sellerModeration"));

// Admin pages (heavy: amcharts4 dependencies)
const AdminDashboard = React.lazy(() => import("./pages/Admin/AdminDashboard"));
const CampaignManagement = React.lazy(() => import("./pages/Admin/CampaignManagement"));
const PlatformSettings = React.lazy(() => import("./pages/Admin/PlatformSettings"));
const TariffManagement = React.lazy(() => import("./pages/Admin/TariffManagement"));
const UserManagement = React.lazy(() => import("./pages/Admin/UserManagement"));
const DisputeManagement = React.lazy(() => import("./pages/Admin/DisputeManagement"));
const AdminReports = React.lazy(() => import("./pages/Admin/AdminReports"));
const ModerationQueue = React.lazy(() => import("./pages/Admin/ModerationQueue"));
const EcoApprovals = React.lazy(() => import("./pages/Admin/EcoApprovals"));
const AnnouncementManagement = React.lazy(() => import("./pages/Admin/AnnouncementManagement"));
const SupportManagement = React.lazy(() => import("./pages/Admin/SupportManagement"));
const ShippingRateManagement = React.lazy(() => import("./pages/Admin/ShippingRateManagement"));
const TaxAccounting = React.lazy(() => import("./pages/Admin/TaxAccounting"));
const TaxReports = React.lazy(() => import("./pages/Admin/TaxReports"));
const AdManagement = React.lazy(() => import("./pages/Admin/AdManagement"));
const QAChecklistPage = React.lazy(() => import("./pages/Admin/QAChecklistPage"));
const PlatformHealth = React.lazy(() => import("./pages/Admin/PlatformHealth"));
const SearchAnalytics = React.lazy(() => import("./pages/Admin/SearchAnalytics"));
const CertificationReviewQueue = React.lazy(() => import("./pages/Admin/CertificationReviewQueue"));
const SellerCertificationsPage = React.lazy(() => import("./pages/Seller/sellerCertifications"));
const SellerApplicationsPage = React.lazy(() => import("./pages/Admin/SellerApplications"));

// Other lazy-loaded pages
const AddSponsorshipProduct = React.lazy(() => import("./stories/es/Sponsorship/AddSponsorshipProduct"));
const GuestShippingSurvey = React.lazy(() => import("./stories/es/Sponsorship/GuestShippingSurvey"));
const Notfound = React.lazy(() => import("./pages/Notfound").then(m => ({ default: m.Notfound })));

const Wrapper = ({ children }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return children;
};
function App({ token }) {
  const dispatch = useDispatch();
  useEffect(() => {
    if (token) {
      dispatch(userActions.getCartDetails(token));
    }
    dispatch(
      userActions.currencyConverter({
        toCurrency: "USD",
        amount: 1000,
      })
    );
  }, [dispatch, token]);

  return (
    <div className="App main-s">
      <Wrapper>
        <AnnouncementBanner />
        <UserProvider>
          <React.Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh'}}>Loading...</div>}>
          <Routes>
            <Route path={ROUTES.HOME} element={<Landing />} />
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.REGISTER} element={<Register />} />
            <Route path={ROUTES.ABOUT_US} element={<Aboutus />} />
            <Route path={ROUTES.CAREERS} element={<Careers />} />
            <Route path={ROUTES.CAREERS_DETAIL + "/:id"} element={<CareersDetail />} />
            <Route path={ROUTES.SEARCH} element={<Search />} />
            <Route path={ROUTES.MANUFACTURING} element={<Manufacturing />} />
            <Route path={ROUTES.ECO_FRIENDLY} element={<EcoFriendlyPage />} />
            <Route
              path={ROUTES.COMPANY_PROFILE + "/:id"}
              element={<CompanyProfile />}
            />
            <Route
              path={ROUTES.CATEGORY + "/:slug/*"}
              element={<CategoryPage />}
            />
            <Route
              path={ROUTES.PRODUCTSINGLE + "/:id"}
              element={<ProductSingle />}
            />
            <Route path={ROUTES.COMPARE} element={<Compare />} />
            <Route
              path={ROUTES.SHARED_WISHLIST + "/:token"}
              element={<SharedWishlist />}
            />
            <Route
              path={ROUTES.SHARED_WISHLIST_FOLDER + "/:token"}
              element={<SharedWishlist />}
            />
            <Route path={ROUTES.WAITINGLIST} element={<Landing />} />
            <Route path={ROUTES.VERIFYOTP} element={<VerifyOTP />} />
            <Route path={ROUTES.CHANGEPASSWORD} element={<ChangePassword />} />
            <Route path={ROUTES.RESETPASSWORD} element={<ResetPassword />} />
            <Route path={ROUTES.CAREERS_FORM} element={<CareersApply />} />
            <Route path={ROUTES.TERMS} element={<Terms />} />
            <Route path="/terms-of-service" element={<Terms />} />
            <Route path={ROUTES.PRIVACY} element={<Privacy />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path={ROUTES.CONTACT} element={<Contact />} />
            <Route path={ROUTES.CHANGELOG} element={<Changelog />} />
            <Route path={ROUTES.STATS} element={<Stats />} />
            <Route path={ROUTES.SUCCESS_STORIES} element={<SuccessStoriesPage />} />
            <Route path={ROUTES.BUYER_PROTECTION} element={<BuyerProtectionPage />} />
            <Route path={ROUTES.GIFT_CARDS} element={<GiftCardsPage />} />
            <Route
              path={ROUTES.GUEST_SHIPPING_SURVEY + "/:reference"}
              element={<GuestShippingSurvey />}
            />
            <Route element={<BuyersProtectedPages />}>
              <Route path={ROUTES.ACCOUNT} element={<Account />} />
              <Route path={ROUTES.PRIVACY_CENTER} element={<PrivacyCenterPage />} />
              <Route path={ROUTES.ADDPAYMENT} element={<AddPayment />} />
              <Route path={ROUTES.BUYER_CATALOG} element={<BuyerCatalog />} />
              <Route
                path={ROUTES.BUYER_DASHBOARD}
                element={<BuyerDashboard />}
              />
              <Route path={ROUTES.BUYER_ORDER} element={<BuyerOrder />} />
              <Route path={ROUTES.BUYER_RETURNS} element={<BuyerReturnsPage />} />
              <Route path={ROUTES.BUYER_DISPUTES} element={<BuyerDisputesPage />} />
              <Route path={ROUTES.BUYER_DISPUTE_DETAIL} element={<BuyerDisputeDetailPage />} />
              <Route path={ROUTES.BUYER_SUBSCRIPTIONS} element={<BuyerSubscriptions />} />
              <Route path={ROUTES.BUYER_SAMPLES} element={<BuyerSamplesPage />} />
              <Route path={ROUTES.BUYER_SAVED_SEARCHES} element={<BuyerSavedSearches />} />
              <Route path={ROUTES.BUYER_PRICE_ALERTS} element={<BuyerPriceAlerts />} />
              <Route path={ROUTES.BUYER_WAITLIST} element={<BuyerWaitlist />} />
              <Route path={ROUTES.BUYER_FOLLOWING} element={<BuyerFollowing />} />
              <Route path={ROUTES.BACKED_CAMPAIGNS} element={<BackedCampaignsPage />} />
              <Route path={ROUTES.CREDIT_WALLET} element={<CreditWalletPage />} />
              <Route path={ROUTES.BUYER_LOYALTY} element={<BuyerLoyaltyPage />} />
              <Route
                path={ROUTES.BUYER_TRANSACTION}
                element={<BuyerTransaction />}
              />

              {/********** sponserShip Page Routes **********/}
              <Route path={ROUTES.SPONSORSHIP} element={<Sponsorship />} />
              <Route
                path={ROUTES.ADD_SPONSORSHIP_PRODUCT}
                element={<AddSponsorshipProduct />}
              />
              <Route
                path={ROUTES.ADD_SPONSORSHIP_PRODUCT_2}
                element={<AddSponsorshipProduct />}
              />
              <Route
                path={ROUTES.ADD_SPONSORSHIP_PRODUCT_3}
                element={<AddSponsorshipProduct />}
              />

              {/********** sponserShip Page Routes **********/}


              <Route path={ROUTES.CART} element={<Cart />} />
              <Route path={ROUTES.CHECKOUT} element={<Checkout />} />
              <Route path={ROUTES.INBOX} element={<Inbox />} />
              <Route path={ROUTES.PRODUCTDETAIL} element={<ProductDetail />} />
              <Route path={ROUTES.MESSAGE} element={<Message />} />
              <Route
                path={ROUTES.MESSAGE_SINGLE + "/:metadata"}
                element={<MessageSingle />}
              />
              <Route path={ROUTES.ORDER} element={<Order />} />
              <Route path={ROUTES.PENDINGREVIEW} element={<Pending />} />

              <Route path={ROUTES.RECEIPT} element={<Receipt />} />
              <Route
                path={ROUTES.RECENTLYVIEWED}
                element={<RecentlyViewed />}
              />
              <Route path={ROUTES.SAVEDITEM} element={<SavedItem />} />

              <Route path={ROUTES.ORDERTRACK} element={<Track />} />
              <Route path={ROUTES.RATEPRODUCT} element={<RateProduct />} />
              <Route path={ROUTES.BUYER_QUOTES} element={<BuyerQuotes />} />
              <Route path={ROUTES.BUYER_RFQS} element={<BuyerRfqsPage />} />
              <Route path={ROUTES.CREATE_RFQ} element={<CreateRfqPage />} />
              <Route path={ROUTES.NOTIFICATION_CENTER} element={<NotificationCenterPage />} />
              <Route path={ROUTES.NOTIFICATION_PREFERENCES} element={<NotificationPreferencesPage />} />
              <Route path={ROUTES.REFERRAL_DASHBOARD} element={<ReferralDashboardPage />} />
              <Route path={ROUTES.NDAMESSAGES} element={<NDAMessage />} />
              <Route
                path={ROUTES.NDAMESSAGES + "/:metadata"}
                element={<NDAMessageInner />}
              />

              <Route path={ROUTES.NDAPOPUP} element={<NDAPopup />} />
              <Route path={ROUTES.SELLER_PAYMENTS} element={<PaymentSetupPage />} />
              <Route element={<AdminProtectedPage />}>
                <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />
                <Route path={ROUTES.ADMIN_CAMPAIGNS} element={<CampaignManagement />} />
                <Route path={ROUTES.ADMIN_SETTINGS} element={<PlatformSettings />} />
                <Route path={ROUTES.ADMIN_TARIFFS} element={<TariffManagement />} />
                <Route path={ROUTES.ADMIN_USERS} element={<UserManagement />} />
                <Route path={ROUTES.ADMIN_DISPUTES} element={<DisputeManagement />} />
                <Route path={ROUTES.ADMIN_REPORTS} element={<AdminReports />} />
                <Route path={ROUTES.ADMIN_MODERATION} element={<ModerationQueue />} />
                <Route path={ROUTES.ADMIN_ECO_APPROVALS} element={<EcoApprovals />} />
                <Route path={ROUTES.ADMIN_ANNOUNCEMENTS} element={<AnnouncementManagement />} />
                <Route path={ROUTES.ADMIN_SUPPORT} element={<SupportManagement />} />
                <Route path={ROUTES.ADMIN_SHIPPING_RATES} element={<ShippingRateManagement />} />
                <Route path={ROUTES.ADMIN_TAX_ACCOUNTING} element={<TaxAccounting />} />
                <Route path={ROUTES.ADMIN_TAX_REPORTS} element={<TaxReports />} />
                <Route path={ROUTES.ADMIN_ADS} element={<AdManagement />} />
                <Route path={ROUTES.ADMIN_QA_CHECKLIST} element={<QAChecklistPage />} />
                <Route path={ROUTES.ADMIN_PLATFORM_HEALTH} element={<PlatformHealth />} />
                <Route path={ROUTES.ADMIN_SEARCH_ANALYTICS} element={<SearchAnalytics />} />
                <Route path={ROUTES.ADMIN_CERTIFICATIONS} element={<CertificationReviewQueue />} />
                <Route path={ROUTES.ADMIN_SELLER_APPLICATIONS} element={<SellerApplicationsPage />} />
              </Route>
              <Route
                path={ROUTES.SELLER_REGISTRATION}
                element={<SellerRegistration />}
              />
              <Route
                path={ROUTES.SELLER_REGISTRATION_2}
                element={<SellerRegistration2 />}
              />
              <Route
                path={ROUTES.SELLER_REGISTRATION_3}
                element={<SellerRegistration3 />}
              />
              <Route element={<SellersProtectedPages />}>
                <Route
                  path={ROUTES.SELLER_DASHBOARD}
                  element={<SellerDashboard />}
                />
                <Route
                  path={ROUTES.SELLER_MESSAGE}
                  element={<SellerMessages />}
                />
                <Route
                  path={ROUTES.SELLER_MESSAGE_SINGLE}
                  element={<SellerMessagesSingle />}
                />
                <Route path={ROUTES.SELLER_ORDER} element={<SellerOrder />} />
                <Route
                  path={ROUTES.SELLER_ORDER_2}
                  element={<SellerOrder2 />}
                />
                {/* <Route path={ROUTES.SELLER_ORDER_3} element={<SellerOrder3 />} /> */}
                <Route path={ROUTES.LISTING} element={<Listing />} />
                <Route path={ROUTES.SIGNING_HISTORY} element={<SigningHistoryPage />} />
                <Route
                  path={ROUTES.LISTING_DETAIL}
                  element={<ListingDetail />}
                />
                <Route
                  path={ROUTES.LISTING_DETAIL + "/:slug"}
                  element={<ListingDetail />}
                />

                <Route
                  path={ROUTES.MARKETINGSUMMARY}
                  element={<MarketingSummary />}
                />
                <Route path={ROUTES.MARKETING} element={<Marketing />} />
                <Route
                  path={ROUTES.MARKETINGCAMPAIGN}
                  element={<MarketingCampaign />}
                />
                <Route
                  path={ROUTES.MARKETINGINVENTORY}
                  element={<MarketingInventory />}
                />
                <Route
                  path={ROUTES.MARKETINGIPRICERANGE}
                  element={<MarketingPriceRange />}
                />
                <Route path={ROUTES.PERFORMANCE} element={<Performance />} />
                <Route path={ROUTES.NDA_PROPOSALS} element={<NDAProposals />} />
                <Route path={ROUTES.SELLER_VERIFICATION} element={<SellerVerification />} />
                <Route path={ROUTES.SELLER_BOOST} element={<SellerBoost />} />
                <Route path={ROUTES.SELLER_PAYOUTS} element={<SellerPayouts />} />
                <Route path={ROUTES.SELLER_QUOTES} element={<SellerQuotes />} />
                <Route path={ROUTES.SELLER_STORE_SETTINGS} element={<SellerStoreSettings />} />
                <Route path={ROUTES.SELLER_BUNDLES} element={<SellerBundles />} />
                <Route path={ROUTES.SELLER_COLLECTIONS} element={<SellerCollections />} />
                <Route path={ROUTES.SELLER_BULK_UPLOAD} element={<BulkUpload />} />
                <Route path={ROUTES.SELLER_BULK_PRICE_UPDATE} element={<BulkPriceUpdatePage />} />
                <Route path={ROUTES.SELLER_BULK_INVENTORY_UPDATE} element={<BulkInventoryUpdatePage />} />
                <Route path={ROUTES.SELLER_DISPUTES} element={<SellerDisputesPage />} />
                <Route path={ROUTES.SELLER_RETURNS} element={<SellerReturnsPage />} />
                <Route path={ROUTES.SELLER_PRODUCT_ANALYTICS} element={<ProductAnalyticsPage />} />
                <Route path={ROUTES.SELLER_ADS} element={<SellerAdsPage />} />
                <Route path={ROUTES.CAMPAIGN_BACKERS} element={<CampaignBackersPage />} />
                <Route path={ROUTES.MANUFACTURER_RFQS} element={<ManufacturerRfqsPage />} />
                <Route path={ROUTES.RFQ_BOARD} element={<RFQBoardPage />} />
                <Route path={ROUTES.RFQ_ANALYTICS} element={<RFQAnalyticsPage />} />
                <Route path={ROUTES.SELLER_ANALYTICS} element={<SellerAnalyticsPage />} />
                <Route path={ROUTES.SELLER_EARNINGS} element={<SellerEarningsPage />} />
                <Route path={ROUTES.SELLER_SAMPLES} element={<SellerSamplesPage />} />
                <Route path={ROUTES.SELLER_REPORTS} element={<SellerReportsPage />} />
                <Route path={ROUTES.SELLER_CERTIFICATIONS} element={<SellerCertificationsPage />} />
                <Route path={ROUTES.SELLER_SUBSCRIPTION} element={<SellerSubscriptionPage />} />
                <Route path={ROUTES.SELLER_MODERATION} element={<SellerModerationPage />} />
              </Route>
            </Route>
            <Route path="/*" element={<Notfound />} />
          </Routes>
          </React.Suspense>
          <CompareBar />
          <SupportChatWidget />
          <CookieConsent />
          <UpsellPopup />
        </UserProvider>
      </Wrapper>
    </div>
  );
}

function mapStateToProps(state) {
  const { authentication } = state;
  return {
    token: authentication.user?.cart_token,
  };
}

const cartToken = connect(mapStateToProps)(App);

export { cartToken as App };
